import {z} from '@hono/zod-openapi';

import {ASSISTANT_BUSINESS_CASE_EXCERPT_MAX_CHARACTERS} from '../constants/ASSISTANT_BUSINESS_CASE_EXCERPT_MAX_CHARACTERS';
import {ASSISTANT_INTEREST_PLANNED_ACTIVITIES_MAX_ITEMS} from '../constants/ASSISTANT_INTEREST_PLANNED_ACTIVITIES_MAX_ITEMS';
import {ASSISTANT_INTEREST_TEXT_MAX_CHARACTERS} from '../constants/ASSISTANT_INTEREST_TEXT_MAX_CHARACTERS';
import {ASSISTANT_INTEREST_TIMELINE_MAX_EVENTS} from '../constants/ASSISTANT_INTEREST_TIMELINE_MAX_EVENTS';
import {BUSINESS_CASE_ACTIONS} from '../constants/BUSINESS_CASE_ACTIONS';
import {BUSINESS_CASE_FACTS_KINDS} from '../constants/BUSINESS_CASE_FACTS_KINDS';
import {BUSINESS_CASE_PLANNED_ACTIVITY_KINDS} from '../constants/BUSINESS_CASE_PLANNED_ACTIVITY_KINDS';
import {BUSINESS_CASE_SIGNAL_CODES} from '../constants/BUSINESS_CASE_SIGNAL_CODES';
import {BUSINESS_CASE_STATES} from '../constants/BUSINESS_CASE_STATES';
import {BUSINESS_CASE_TIMELINE_EVENT_DIRECTIONS} from '../constants/BUSINESS_CASE_TIMELINE_EVENT_DIRECTIONS';
import {BUSINESS_CASE_TIMELINE_EVENT_KINDS} from '../constants/BUSINESS_CASE_TIMELINE_EVENT_KINDS';
import {BUSINESS_CASE_TYPES} from '../constants/BUSINESS_CASE_TYPES';
import {CUSTOMER_EMAIL_LANGUAGE_SOURCES} from '../constants/CUSTOMER_EMAIL_LANGUAGE_SOURCES';
import {assistantBusinessCaseSignalSchema} from './AssistantBusinessCaseSignalSchema';

const shortText = z.string().min(1).max(ASSISTANT_INTEREST_TEXT_MAX_CHARACTERS);
const timestamp = z.string().min(1);
const date = z.string().date();
const dateTime = z.string().datetime({offset: true});
const moneySchema = z.object({
  amount: z.number(),
  currency: shortText,
});

/**
 * Bounded business-case facts published by the mounted DMS page. The whole slice is untrusted
 * model input; identifiers and permissions are revalidated by Prompt Bridge and DMS actions.
 */
export const assistantBusinessCaseContextSchema = z
  .object({
    businessCaseId: shortText,
    publicId: shortText.nullable().describe('Human-readable business-case identifier when known.'),
    branchId: shortText,
    customerId: shortText,
    contactPersonId: shortText
      .nullable()
      .describe('Selected contact person when known.')
      .optional(),
    businessCaseType: z.enum(BUSINESS_CASE_TYPES),
    businessCaseState: z.enum(BUSINESS_CASE_STATES),
    createdAt: timestamp,
    updatedAt: timestamp,
    customerLanguage: shortText.nullable().describe('Resolved customer language when available.'),
    userLanguage: shortText,
    customer: z
      .object({
        id: shortText,
        displayName: shortText.optional(),
        preferredLocale: shortText
          .nullable()
          .describe('BCP-47 locale for customer-facing drafts when known.'),
        languageSource: z.enum(CUSTOMER_EMAIL_LANGUAGE_SOURCES),
      })
      .optional(),
    sender: z
      .object({
        displayName: shortText.optional(),
        hasSignature: z.boolean(),
      })
      .optional(),
    origin: z
      .object({
        interestId: shortText.optional(),
        sourceLabel: shortText.optional(),
      })
      .optional(),
    facts: z.object({
      kind: z.enum(BUSINESS_CASE_FACTS_KINDS),
      saleVehicle: z
        .object({
          vehicleId: shortText,
          make: shortText.optional(),
          model: shortText.optional(),
          year: z.number().int().nonnegative().optional(),
          registrationPlate: shortText.optional(),
          vin: shortText.optional(),
          priceWithVat: moneySchema.optional(),
          priceWithoutVat: moneySchema.optional(),
          discountAmount: moneySchema.optional(),
          discountPercentage: shortText.optional(),
          isAvailable: z.boolean().optional(),
          isReserved: z.boolean().optional(),
          isFinanced: z.boolean().optional(),
          financingEndsOn: date.optional(),
        })
        .optional(),
      purchaseVehicle: z
        .object({
          vehicleId: shortText.optional(),
          tradeType: z.enum(['IN_STOCK', 'BROKERAGE']),
          expectedPurchaseOn: date.optional(),
          customerExpectedPrice: moneySchema.optional(),
          maxPurchasePrice: moneySchema.optional(),
        })
        .optional(),
      onlineOffer: z
        .object({
          offerId: shortText.optional(),
          firstSentAt: dateTime.optional(),
          validTo: dateTime.optional(),
          openCount: z.number().int().nonnegative().optional(),
          lastOpenedAt: dateTime.optional(),
        })
        .optional(),
      availableActions: z.array(z.enum(BUSINESS_CASE_ACTIONS)).default([]),
      /** Present only when the current employee has permission to see margin. */
      margin: moneySchema.optional(),
      incompleteChecklistItemCount: z
        .number()
        .int()
        .nonnegative()
        .nullable()
        .describe('Verified incomplete checklist count when the checklist source loaded.'),
      payments: z
        .array(
          z.object({
            id: shortText,
            kind: z.enum(['deposit', 'invoice']),
            amount: z.number().optional(),
            currency: shortText.optional(),
            issuedAt: timestamp.nullable().describe('Verified payment issue timestamp.'),
            dueOn: timestamp.nullable().describe('Verified payment due timestamp.').optional(),
            paidAt: timestamp.nullable().describe('Verified payment timestamp.'),
            state: z.enum(['unpaid', 'paid', 'unknown']),
          })
        )
        .default([]),
      completeness: z.object({
        timelineComplete: z.boolean(),
        omittedTimelineEventCount: z.number().int().nonnegative(),
        plannedActivitiesComplete: z.boolean(),
        omittedPlannedActivityCount: z.number().int().nonnegative().optional(),
        checklistComplete: z.boolean(),
        paymentEvidenceComplete: z.boolean(),
        offerEvidenceComplete: z.boolean(),
        vehicleLoaded: z.boolean().default(false),
        actionsLoaded: z.boolean().default(false),
        checkoutLoaded: z.boolean().default(false),
        checklistLoaded: z.boolean().default(false),
        testDrivesLoaded: z.boolean().default(false),
        messagesLoaded: z.boolean().default(false),
      }),
    }),
    /** Newest-first. */
    timeline: z
      .array(
        z.object({
          id: shortText,
          occurredAt: timestamp,
          direction: z.enum(BUSINESS_CASE_TIMELINE_EVENT_DIRECTIONS),
          kind: z.enum(BUSINESS_CASE_TIMELINE_EVENT_KINDS),
          subject: shortText.optional(),
          /** Sanitised, quoted-reply-collapsed plain text. Customer-authored. Untrusted. */
          excerpt: z.string().min(1).max(ASSISTANT_BUSINESS_CASE_EXCERPT_MAX_CHARACTERS).optional(),
          excerptTruncated: z.boolean().optional(),
        })
      )
      .max(ASSISTANT_INTEREST_TIMELINE_MAX_EVENTS)
      .default([]),
    plannedActivities: z
      .array(
        z.object({
          id: shortText,
          kind: z.enum(BUSINESS_CASE_PLANNED_ACTIVITY_KINDS),
          dueAt: timestamp.nullable().describe('Scheduled timestamp when the activity has one.'),
          completed: z.boolean(),
          title: shortText.optional(),
        })
      )
      .max(ASSISTANT_INTEREST_PLANNED_ACTIVITIES_MAX_ITEMS)
      .default([]),
    /** Deterministic output of detectBusinessCaseSignals. Never model-authored. */
    signals: z
      .array(assistantBusinessCaseSignalSchema)
      .max(BUSINESS_CASE_SIGNAL_CODES.length)
      .default([]),
    policy: z.object({
      coldThresholdDays: z.number().int().positive(),
      awaitingReplyMinimumHours: z.number().int().nonnegative(),
      offerNotSentMinimumDays: z.number().int().nonnegative(),
      offerSentNoResponseMinimumDays: z.number().int().nonnegative(),
      offerExpiringDays: z.number().int().nonnegative(),
      testDriveFollowUpMinimumDays: z.number().int().nonnegative(),
      testDriveSoonHours: z.number().int().nonnegative(),
      paymentUnpaidMinimumDays: z.number().int().nonnegative(),
    }),
    evaluatedAt: timestamp,
  })
  .openapi('AssistantBusinessCaseContext');

export type AssistantBusinessCaseContext = z.infer<typeof assistantBusinessCaseContextSchema>;
